/**
 * BoothPreferencesPage - Page for managing vendor booth preferences
 * Issue #677 - Vendor Booth Preferences & Smart Matching
 */

import React, { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Settings, Loader2 } from 'lucide-react';
import { VendorContext } from '../contexts/VendorContext';
import BoothPreferencesForm from './BoothPreferencesForm';
import VendorAuthGuard from './VendorAuthGuard';

function BoothPreferencesPageContent() {
  const navigate = useNavigate();
  const { vendorId } = useParams<{ vendorId: string }>();
  const vendorContext = useContext(VendorContext);
  const vendors = vendorContext?.vendors || [];
  const isLoading = vendorContext?.isLoading || false;
  const loadVendors = vendorContext?.loadVendors;

  // Load vendors on mount if not already loaded
  useEffect(() => {
    if (loadVendors && vendors.length === 0 && !isLoading) {
      loadVendors();
    }
  }, [loadVendors, vendors.length, isLoading]);

  const selectedVendor = vendors.find(v => v.id === Number(vendorId));

  // Show loading state while vendors are being fetched
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
          <span className="ml-2 text-gray-600">Loading vendor information...</span>
        </div>
      </div>
    );
  }

  if (!vendorId || !selectedVendor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Vendor not found</h2>
          <p className="text-red-700 mb-4">
            The vendor profile you're looking for doesn't exist or you don't have access to it.
          </p>
          <button
            onClick={() => navigate('/vendor/dashboard')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/vendor/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-moonbone rounded-lg">
            <Settings className="w-6 h-6 text-secondary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Booth Preferences</h1>
            <p className="text-gray-600 mt-1">
              {selectedVendor.business_name}
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-moonbone border border-teal-200 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-ink mb-2">How it works</h3>
        <ul className="text-sm text-ink space-y-1">
          <li>• Save your booth preferences once and get personalized recommendations at every fair</li>
          <li>• We'll score and rank available booths based on what matters most to you</li>
          <li>• You can always update your preferences or view all booths at any time</li>
        </ul>
      </div>

      {/* Preferences Form */}
      <BoothPreferencesForm
        vendorId={Number(vendorId)}
        onSaveSuccess={() => {
          // Could show a success message or redirect
          console.log('Preferences saved successfully');
        }}
      />
    </div>
  );
}

export default function BoothPreferencesPage() {
  return (
    <VendorAuthGuard>
      <BoothPreferencesPageContent />
    </VendorAuthGuard>
  );
}
